import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import { offeneDurchfuehrungen, type OffeneDurchfuehrung } from '../api/termine';
import { Meldung } from '../komponenten/Meldung';
import { Terminfenster } from './Terminfenster';

/**
 * DURCHFÜHRUNG NOCH NICHT BESTÄTIGT (Tabelle 27, Zeile 9; BUC-05; Auftrag CRM-111).
 *
 * ============================================================================
 * EINE LISTE STATT EINES NÄCHTLICHEN LAUFS
 * ============================================================================
 *
 * Zeile 9 verlangt zweierlei: „Das Terminende ist erreicht UND die
 * Durchführung ist bestätigt." STA-01 schließt jeden anderen Wechsel aus,
 * „weder durch Bedienung noch durch einen Systemvorgang". Ein Lauf, der nach
 * der Uhr umstellt, bestätigte nichts. Hier steht deshalb, was zu bestätigen
 * ist; bestätigt wird im Terminfenster, mit Rückfrage.
 *
 * Einspaltig, eine Zeile je Termin — liest sich ab 360 px (ÄA-63). Ist nichts
 * offen, steht hier nichts: Eine leere Karte wäre nur Lärm.
 */
export function Durchfuehrungsliste({
  darfAbsagen,
  darfOptionBeantworten,
  darfWartelisteSehen = false,
  beiTerminakte,
}: {
  readonly darfAbsagen: boolean;
  readonly darfOptionBeantworten: boolean;
  readonly darfWartelisteSehen?: boolean;
  /** CRM-117 (L6): der Weg in die Terminakte; ohne ihn bleibt das Fenster ohne Verweis. */
  readonly beiTerminakte?: (termin: string) => void;
}): JSX.Element | null {
  const [termine, setzeTermine] = useState<readonly OffeneDurchfuehrung[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [offen, setzeOffen] = useState<string | null>(null);

  const holen = useCallback(async (): Promise<void> => {
    try {
      setzeTermine(await offeneDurchfuehrungen());
      setzeFehler(null);
    } catch (e) {
      if (!(e instanceof ApiFehler)) throw e;
      setzeFehler(e);
    }
  }, []);

  useEffect(() => {
    void holen();
  }, [holen]);

  if (fehler === null && termine.length === 0) return null;

  return (
    <div className="karte" aria-labelledby="durchfuehrung-offen">
      <h2 id="durchfuehrung-offen">Durchführung noch nicht bestätigt</h2>
      {fehler !== null && <Meldung fehler={fehler} />}
      {termine.length > 0 && (
        <>
          <p className="hilfe">
            {termine.length === 1
              ? 'Ein Termin ist vorbei, seine Durchführung ist nicht bestätigt.'
              : `${String(termine.length)} Termine sind vorbei, ihre Durchführung ist nicht bestätigt.`}{' '}
            Erst die Bestätigung schließt den Termin ab.
          </p>
          <ul className="optionen__stand">
            {termine.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className="knopf knopf--verweis"
                  onClick={() => {
                    setzeOffen(t.id);
                  }}
                >
                  {t.produkt}
                </button>{' '}
                <span className="hilfe">
                  {new Date(t.beginn).toLocaleString('de-DE', {
                    weekday: 'short',
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'Europe/Berlin',
                  })}
                  {t.ort === null ? '' : ` · ${t.ort}`}
                </span>{' '}
                <span className={t.anwesenheitErfasst < t.teilnehmende ? 'marke marke--warn' : 'marke'}>
                  {t.teilnehmende === 0
                    ? 'ohne Teilnehmende'
                    : `Anwesenheit ${String(t.anwesenheitErfasst)} von ${String(t.teilnehmende)}`}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {offen !== null && (
        <Terminfenster
          termin={offen}
          darfAbsagen={darfAbsagen}
          darfOptionBeantworten={darfOptionBeantworten}
          darfVereinbarungAendern
          darfWartelisteSehen={darfWartelisteSehen}
          {...(beiTerminakte === undefined ? {} : { beiAkte: beiTerminakte })}
          beiSchliessen={() => {
            setzeOffen(null);
          }}
          beiAenderung={() => {
            void holen();
          }}
        />
      )}
    </div>
  );
}
